import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import '../styles.css';
import './MaintenanceForm.css';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { resolveActiveCustomerContext } from '../auth/customerContext';
import { authHeaders } from '../api/headers';
import { apiUrl } from '../api/http';
import {
  Form,
  Input,
  DatePicker,
  Radio,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Divider,
  Card,
  Tooltip,
  Modal,
} from 'antd';
import { ArrowRightOutlined, CheckCircleOutlined, InfoCircleOutlined, PrinterOutlined, UploadOutlined } from '@ant-design/icons';
import PrintMaintenanceForm from './PrintMaintenanceForm';
import { getRecordReturnTarget } from '../recordReturnNavigation';
import dayjs from 'dayjs';
import RecordAttachments from '../components/RecordAttachments';
import { DashboardPageShell, DashboardPanel, DashboardPrimaryActionButton } from '../components/dashboard';
import { ExternalSubmittedFormRecord, PrintExternalSubmittedFormSummary } from '../components/ExternalSubmittedFormRecord';

const extractForklift = (raw, fallbackUnitId) => {
  const pickId = (o) =>
    o?.id ??
    o?.forkliftId ??
    o?.forklift_id ??
    o?.ForkliftId ??
    o?.ForkliftID ??
    o?.forklift?.id ??
    null;

  const src = Array.isArray(raw) ? (raw[0] ?? {}) : raw ?? {};
  const idRaw = pickId(src);
  const idNum = idRaw != null ? Number(idRaw) : null;

    return {
      id: Number.isFinite(idNum) ? idNum : '',
      status: src.status ?? src.forklift?.status ?? '',
      customerName: src.customerName ?? '',
      address: src.customerAddress ?? '',
      city: src.customerCity ?? '',
      state: src.customerState ?? '',
      customerPO: '',
      unitId: src.unitId ?? fallbackUnitId ?? '',
      make: src.make ?? '',
      model: src.model ?? '',
      serialNumber: src.serialNumber ?? '',
      hourMeter: src.hourMeter ?? '',
    };
};

const PFN = (
  <Radio.Group
    className="maintenance-pfn-control"
    options={[
      { label: 'Pass', value: 'P' },
      { label: 'Fail', value: 'F' },
      { label: 'N/A', value: 'N' },
    ]}
    optionType="button"
    buttonStyle="solid"
  />
);

const PRINT_SECTIONS = [
  {
    title: '1. Visual Inspection',
    items: [
      'Fluid Leaks',
      'Carriage Rollers / Slide Blocks',
      'Mast – Chains / Retainers / Sheaves / Anchors',
      'Forks and Fork Lock Pins',
      'Hydraulic Cylinders',
      'Brake Master Cylinder',
      'Over Mast Hoses and Cables',
      'Caster Assembly',
      'Drive Unit / Radial Rings',
      'Hydraulic Manifold / Piping',
      'Tractor Frame / Base Legs / Doors & Covers',
    ],
  },
  {
    title: '2. Hydraulic System',
    items: [
      'Lift Cyl.',
      'Reach Cyl.',
      'Tilt Cyl.',
      'Sideshift Cyl.',
      'Connections',
      'Relief Pressures',
      'Lift',
      'Aux',
      'Steer',
    ],
  },
  {
    title: '3. Electrical System',
    items: [
      'Control Wiring',
      'Switches',
      'Contactors',
      'Power Cables',
      'Fuses',
      'Connectors',
      'System Short to Frame',
    ],
  },
  { title: '4. Motors — Drive', items: ['Brushes', 'Comm', 'Amps', 'Ohms'] },
  { title: '4. Motors — Lift', items: ['Brushes', 'Comm', 'Amps', 'Ohms'] },
  { title: '4. Motors — Aux', items: ['Brushes', 'Comm', 'Amps', 'Ohms'] },
  { title: '4. Motors — Steer', items: ['Brushes', 'Comm', 'Amps', 'Ohms'] },
  {
    title: '5. Battery',
    items: [
      'Volts Loaded',
      'Volts No Load',
      'Pos. Leakage',
      'Neg. Leakage',
      'S.G.',
      'Electrolyte level',
    ],
  },
  {
    title: '6. Operational Inspection',
    items: [
      'Braking Distance',
      'Plugging Distance',
      'Travel System',
      'Lift / Lower System',
      'Auxiliary System',
      'Steering System',
      'Mini Mast Functions',
      'Wire Guidance',
    ],
  },
  {
    title: '7. Safety',
    items: [
      'Horn',
      'Travel Alarm',
      'Lights – Warning / Brake',
      'Flood Lights',
      'Emergency Disconnect',
      'Static Straps',
      'Mast – Stops / Attach Bolts',
      'Load Backrest',
      'Fork Condition',
      'Lift Chains',
      'Brake Pads / Shoes',
      'Brake Disk / Drums',
      'Parking Brake',
      'Steering System',
      'Warning Decals',
      'Data Plate',
      'Tire Condition',
      'Overhead Guard',
      'Safety Belt / Tether',
      'Safety Glass',
    ],
  },
  {
    title: '8. Lubrication',
    items: [
      'Hydraulic Fluid',
      'Oil Filter',
      'Drive Unit / Gear Oil',
      'Brake Fluid Reservoir',
      'Steer Linkage',
      'Mast / Chains / Bearings',
      'Scissor Assembly',
      'Carriage / Attachments',
      'Load Wheels',
      'Caster Unit',
      'Battery Rollers',
      'Pump Couplings',
    ],
  },
];

const hasFailures = (obj) =>
  Object.values(obj || {}).some((v) => {
    const s = String(v || '').toUpperCase();
    return s === 'F' || s === 'FAIL' || s === 'BROKEN';
  });

export default function MaintenanceForm() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();

  const [form] = Form.useForm();
  const [externalForm] = Form.useForm();

  const q = new URLSearchParams(location.search);
  const recordId = q.get('id');
  const draftId = q.get('draftId');

  const [isReadOnly, setIsReadOnly] = useState(false);
  const [savedRecordId, setSavedRecordId] = useState(null);
  const attachmentsRef = useRef(null);
  const externalAttachmentsRef = useRef(null);
  const [formPath, setFormPath] = useState(recordId || draftId ? 'native' : null);
  const [submissionSource, setSubmissionSource] = useState('');
  const [maintenanceRecordDetail, setMaintenanceRecordDetail] = useState(null);

  useEffect(() => {
    if (!recordId) return;

    fetch(apiUrl(`/api/maintenance/${recordId}`), {
      headers: authHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        dirtyInitRef.current = false;
        try {
          localStorage.removeItem('dirtyForm');
        } catch {}
        setIsDirty(false);

        setSubmissionSource(data.submissionSource || 'MANAGELIFT_FORM');
        setMaintenanceRecordDetail(data);

        form.setFieldsValue({
          customer_name: data.customerName || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          customer_po: data.customerPO || '',
          dateStarted: data.dateStarted ? dayjs(data.dateStarted) : null,
          dateCompleted: data.dateCompleted ? dayjs(data.dateCompleted) : null,
          unit_number: data.unitId || '',
          make: data.make || '',
          model: data.model || '',
          serial_number: data.serialNumber || '',
          hour_meter: data.hourMeter ?? '',
          ...(data.inspectionData || {}),
          technician_comments: data.technicianComments || '',
          technician_signature: data.technicianSignature || '',
        });

        setTimeout(() => {
          dirtyInitRef.current = true;
        }, 0);

        setPerformedByName(data.performedByName || data.technicianName || '');
        setIsReadOnly(true);
      })
      .catch(() => {});
  }, [recordId, form]);

  useEffect(() => {
    if (!draftId) return;

    const idNum = Number(draftId);
    if (!Number.isFinite(idNum)) return;

    fetch(apiUrl(`/api/maintenance/draft/${idNum}`), {
      headers: authHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Fetch draft failed (${res.status})`);
        return res.json();
      })
      .then((data) => {
        dirtyInitRef.current = false;
        try {
          localStorage.removeItem('dirtyForm');
        } catch {}
        setIsDirty(false);

        form.setFieldsValue({
          customer_name: data.customerName || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          customer_po: data.customerPO || '',
          dateStarted: data.dateStarted ? dayjs(data.dateStarted) : null,
          dateCompleted: data.dateCompleted ? dayjs(data.dateCompleted) : null,
          unit_number: data.unitId || '',
          make: data.make || '',
          model: data.model || '',
          serial_number: data.serialNumber || '',
          hour_meter: data.hourMeter ?? '',
          ...(data.inspectionData || {}),
          technician_comments: data.technicianComments || '',
          technician_signature: data.technicianSignature || '',
        });

        setHourTouched(false);

        setTimeout(() => {
          dirtyInitRef.current = true;
        }, 0);

        setIsReadOnly(false);
        setPerformedByName('');
      })
      .catch(() => {});
  }, [draftId, form]);

  const initialCustomerId =
    location.state?.customerId ??
    (() => {
      try {
        const v = localStorage.getItem('selectedCustomerId');
        return v ? Number(v) : null;
      } catch {
        return null;
      }
    })() ??
    null;

  const [forkliftData, setForkliftData] = useState({});
  const [performedByName, setPerformedByName] = useState('');
  const [customerId] = useState(initialCustomerId);
  const recordReturnTarget = useMemo(
    () => getRecordReturnTarget(location, session?.role),
    [location, session?.role]
  );

  const [initialHour, setInitialHour] = useState(null);
  const [hourTouched, setHourTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [resultOpen, setResultOpen] = useState(false);
  const [resultTitle, setResultTitle] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [resultStatus, setResultStatus] = useState('IN_SERVICE');

  const [choiceOpen, setChoiceOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [pendingInspection, setPendingInspection] = useState(null);

  const [draftStatusOpen, setDraftStatusOpen] = useState(false);
  const [pendingDraftPayload, setPendingDraftPayload] = useState(null);

  const [isDirty, setIsDirty] = useState(false);
  const dirtyInitRef = useRef(false);

  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const pendingNavRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.removeItem('dirtyForm');
    } catch {}
    setIsDirty(false);
    return () => {
      try {
        localStorage.removeItem('dirtyForm');
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (recordId) return;
    if (!unitId) return;
    fetch(apiUrl(`/api/forklifts/${encodeURIComponent(unitId)}`), {
      headers: authHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Fetch forklift failed (${res.status})`);
        return res.json();
      })
      .then((data) => setForkliftData(extractForklift(data, unitId)))
      .catch(() => {});
  }, [unitId, recordId]);

  useEffect(() => {
    if (recordId) return;

    const hm = Number(forkliftData.hourMeter);
    setInitialHour(Number.isFinite(hm) ? hm : null);

    if (draftId) return;

    dirtyInitRef.current = false;

    form.setFieldsValue({
      customer_name: forkliftData.customerName || '',
      address: forkliftData.address || '',
      city: forkliftData.city || '',
      state: forkliftData.state || '',
      unit_number: forkliftData.unitId || '',
      make: forkliftData.make || '',
      model: forkliftData.model || '',
      serial_number: forkliftData.serialNumber || '',
      hour_meter: forkliftData.hourMeter ?? '',
      dateStarted: dayjs(),
      dateCompleted: dayjs(),
    });

    setTimeout(() => {
      dirtyInitRef.current = true;
    }, 0);

    setHourTouched(false);
  }, [forkliftData, form, recordId, draftId]);

  useEffect(() => {
    if (formPath !== 'external') return;
    externalForm.setFieldsValue({
      customer_name: forkliftData.customerName || '',
      address: forkliftData.address || '',
      city: forkliftData.city || '',
      state: forkliftData.state || '',
      unit_number: forkliftData.unitId || '',
      make: forkliftData.make || '',
      model: forkliftData.model || '',
      serial_number: forkliftData.serialNumber || '',
      hour_meter: externalForm.getFieldValue('hour_meter') ?? (forkliftData.hourMeter ?? ''),
      date: externalForm.getFieldValue('date') || dayjs(),
      status: externalForm.getFieldValue('status') || 'IN_SERVICE',
    });
  }, [formPath, externalForm, forkliftData]);

  const fmtDate = (d) => {
    if (!d) return null;
    if (typeof d === 'string') return d;
    if (typeof d.format === 'function') return d.format('YYYY-MM-DD');
    return null;
  };

  const pKeys = useMemo(
    () => [
      'fluid_leaks',
      'carriage_rollers_slide_blocks',
      'mast_chains_retainers_sheaves_anchors',
      'forks_and_fork_lock_pins',
      'hydraulic_cylinders',
      'brake_master_cylinder',
      'over_mast_hoses_and_cables',
      'caster_assembly',
      'drive_unit_radial_rings',
      'hydraulic_manifold_piping',
      'tractor_frame_base_legs_doors_and_covers',
      'lift_cyl',
      'reach_cyl',
      'tilt_cyl',
      'sideshift_cyl',
      'connections',
      'relief_pressures',
      'lift',
      'aux',
      'steer',
      'control_wiring',
      'switches',
      'contactors',
      'power_cables',
      'fuses',
      'connectors',
      'system_short_to_frame',
      'drive_brushes',
      'drive_comm',
      'drive_amps',
      'drive_ohms',
      'lift_brushes',
      'lift_comm',
      'lift_amps',
      'lift_ohms',
      'aux_brushes',
      'aux_comm',
      'aux_amps',
      'aux_ohms',
      'steer_brushes',
      'steer_comm',
      'steer_amps',
      'steer_ohms',
      'volts_loaded',
      'volts_no_load',
      'pos_leakage',
      'neg_leakage',
      'sg',
      'electrolyte_level',
      'braking_distance',
      'plugging_distance',
      'travel_system',
      'lift_lower_system',
      'auxiliary_system',
      'steering_system',
      'mini_mast_functions',
      'wire_guidance',
      'horn',
      'travel_alarm',
      'lights_warning_brake',
      'flood_lights',
      'emergency_disconnect',
      'static_straps',
      'mast_stops_attach_bolts',
      'load_backrest',
      'fork_condition',
      'lift_chains',
      'brake_pads_shoes',
      'brake_disk_drums',
      'parking_brake',
      'steering_system_safety',
      'warning_decals',
      'data_plate',
      'tire_condition',
      'overhead_guard',
      'safety_belt_tether',
      'safety_glass',
      'hydraulic_fluid',
      'oil_filter',
      'drive_unit_gear_oil',
      'brake_fluid_reservoir',
      'steer_linkage',
      'mast_chains_bearings',
      'scissor_assembly',
      'carriage_attachments',
      'load_wheels',
      'caster_unit',
      'battery_rollers',
      'pump_couplings',
    ],
    []
  );

  const doPost = async (payload, overrideStatus) => {
    const draftIdNum =
      draftId != null && String(draftId).trim() !== '' && Number.isFinite(Number(draftId))
        ? Number(draftId)
        : null;

    const body = {
      ...payload,
      ...(Number.isFinite(draftIdNum) ? { draftId: draftIdNum } : {}),
      ...(overrideStatus ? { statusOverride: overrideStatus } : {}),
    };

    const response = await fetch(apiUrl('/api/maintenance/form'), {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let data = null;
    try {
      data = await response.json();
    } catch {}
    return { response, data };
  };

  const doDraftPost = async (payload) => {
    const response = await fetch(apiUrl('/api/maintenance/draft'), {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    let data = null;
    try {
      data = await response.json();
    } catch {}
    return { response, data };
  };

    const finishSuccess = async (data, inspectionData) => {
      const status =
        data?.forkliftStatus ||
        data?.status ||
        (hasFailures(inspectionData) ? 'DOWN' : 'IN_SERVICE');

      setResultStatus(status);

      if (status === 'RETIRED') {
        setResultTitle('Maintenance Saved');
        setResultMessage('Lift is RETIRED. Status remains RETIRED.');
      } else if (status === 'DOWN') {
        setResultTitle('Maintenance Saved');
        setResultMessage('Lift marked DOWN due to failed checks.');
      } else if (status === 'WAITING_ON_PARTS') {
        setResultTitle('Maintenance Saved');
        setResultMessage('Lift marked WAITING ON PARTS.');
      } else {
        setResultTitle('Maintenance Saved');
        setResultMessage('Maintenance completed. Lift in service.');
      }

      try {
        localStorage.removeItem('dirtyForm');
      } catch {}
      setIsDirty(false);
      const rid = data?.maintenanceLogId || data?.id || null;
      setSavedRecordId(rid);
      if (rid && attachmentsRef.current?.hasPending?.()) {
        const r = await attachmentsRef.current.flushUploads(rid);
        if (!r.ok) {
          setResultTitle('Maintenance Saved');
          setResultMessage('Record was saved, but one or more uploads failed. Retry from the saved record if allowed by the upload panel.');
        }
      }
      setResultOpen(true);
    };

  const onFinish = async (vals) => {
    if (submitting) return;

    const inspectionData = Object.fromEntries(
      Object.entries(vals).filter(([k]) => pKeys.includes(k))
    );

      const payload = {
        forkliftId: forkliftData.id || null,
        customerName: vals.customer_name,
        customerPO: vals.customer_po || '',
        address: vals.address,
        city: vals.city,
        state: vals.state,
        unitId: vals.unit_number,
        make: vals.make,
        model: vals.model,
        serialNumber: vals.serial_number,
        hourMeter: vals.hour_meter,
        dateStarted: fmtDate(vals.dateStarted),
        dateCompleted: fmtDate(vals.dateCompleted),
        description: 'Maintenance form submitted via frontend.',
        technicianComments: vals.technician_comments || '',
        technicianSignature: vals.technician_signature,
        inspectionData,
      };

      const isRetired = String(forkliftData?.status || '').toUpperCase() === 'RETIRED';
      if (isRetired) {
        await submitWith('RETIRED', payload, inspectionData);
        return;
      }

      if (!hasFailures(inspectionData)) {
        await submitWith(null, payload, inspectionData);
        return;
      }


    setPendingPayload(payload);
    setPendingInspection(inspectionData);
    setChoiceOpen(true);
  };

  const submitWith = async (overrideStatus = null, payload, inspectionData) => {
    try {
      setSubmitting(true);
      const { response, data } = await doPost(payload, overrideStatus);
      if (response.ok) {
        await finishSuccess(data, inspectionData);
      } else {
        const msg = data?.message || 'Error submitting form.';
        setResultStatus('ERROR');
        setResultTitle('Error');
        setResultMessage(msg);
        setResultOpen(true);
      }
    } catch {
      setResultStatus('ERROR');
      setResultTitle('Network Error');
      setResultMessage('Network or server error occurred.');
      setResultOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const submitExternalForm = async (vals) => {
    if (submitting) return;
    const externalFiles = externalAttachmentsRef.current?.getPendingFiles?.('EXTERNAL_SUBMITTED_FORM') || [];
    if (externalFiles.length < 1 || externalFiles.length > 3) {
      setResultStatus('ERROR');
      setResultTitle('External form required');
      setResultMessage('Upload at least one and no more than three external submitted maintenance form files/pages before submitting.');
      setResultOpen(true);
      return;
    }
    const status = vals.status || 'IN_SERVICE';
    const hasExternalFailure = status === 'DOWN';
    const payload = {
      forkliftId: forkliftData.id || null,
      customerName: vals.customer_name,
      customerPO: vals.customer_po || '',
      address: vals.address,
      city: vals.city,
      state: vals.state,
      unitId: vals.unit_number,
      make: vals.make,
      model: vals.model,
      serialNumber: vals.serial_number,
      hourMeter: vals.hour_meter,
      dateStarted: fmtDate(vals.date),
      dateCompleted: fmtDate(vals.date),
      description: 'External submitted maintenance form.',
      technicianComments: vals.technician_comments || '',
      technicianSignature: vals.technician_signature,
      inspectionData: hasExternalFailure ? { external_submitted_form_result: 'fail' } : {},
      submissionSource: 'EXTERNAL_FORM',
    };
    const override = status === 'WAITING_ON_PARTS' ? 'WAITING_ON_PARTS' : null;
    try {
      setSubmitting(true);
      const body = { ...payload, ...(override ? { statusOverride: override } : {}) };
      const fd = new FormData();
      fd.append('payload', JSON.stringify(body));
      externalFiles.forEach((file) => fd.append('files', file));
      const response = await fetch(apiUrl('/api/maintenance/external-form'), {
        method: 'POST',
        headers: authHeaders({ multipart: true }),
        body: fd,
      });
      let data = null;
      try { data = await response.json(); } catch {}
      if (!response.ok) {
        setResultStatus('ERROR');
        setResultTitle('Error');
        setResultMessage(data?.message || 'Error submitting external maintenance form.');
        setResultOpen(true);
        return;
      }
      const rid = data?.maintenanceLogId || data?.id || null;
      setSavedRecordId(rid);
      if (rid) {
        const photoResult = await externalAttachmentsRef.current?.flushUploads?.(rid, 'PHOTO');
        if (photoResult && !photoResult.ok) {
          setResultStatus('ERROR');
          setResultTitle('Photo upload failed');
          setResultMessage(photoResult.message || 'External form was saved, but photo attachments failed to upload.');
          setResultOpen(true);
          return;
        }
      }
      setResultStatus(status);
      setResultTitle('Maintenance Saved');
      setResultMessage('External maintenance form submitted.');
      try { localStorage.removeItem('dirtyForm'); } catch {}
      setIsDirty(false);
      setResultOpen(true);
    } catch {
      setResultStatus('ERROR');
      setResultTitle('Network Error');
      setResultMessage('Network or server error occurred.');
      setResultOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const saveDraftWithStatus = async (status, payloadOverride = null) => {
    if (submitting) return;
    const base = payloadOverride || pendingDraftPayload;
    if (!base) return;

    try {
      setSubmitting(true);

      const payload = {
        ...base,
        status,
        workOrderStatus: 'DRAFT',
      };

      const { response, data } = await doDraftPost(payload);

      if (response.ok) {
        if (
          (draftId == null || String(draftId).trim() === '') &&
          data?.id != null &&
          Number.isFinite(Number(data.id))
        ) {
          const nextUnit = encodeURIComponent(payload.unitId || unitId || '');
          navigate(`/maintenance/${nextUnit}?draftId=${Number(data.id)}`, {
            replace: true,
            state: { customerId },
          });
        }

        try {
          localStorage.removeItem('dirtyForm');
        } catch {}
        setIsDirty(false);

        navigate(recordReturnTarget, { replace: true, state: { customerId } });

      } else {
        const msg = data?.message || 'Error saving draft.';
        setResultStatus('ERROR');
        setResultTitle('Error');
        setResultMessage(msg);
        setResultOpen(true);
      }
    } catch {
      setResultStatus('ERROR');
      setResultTitle('Network Error');
      setResultMessage('Network or server error occurred.');
      setResultOpen(true);
    } finally {
      setSubmitting(false);
      setPendingDraftPayload(null);
    }
  };

  const handleFinishLater = async () => {
    if (submitting) return;

    const vals = form.getFieldsValue(true);

    const inspectionData = Object.fromEntries(
      Object.entries(vals)
        .filter(([k]) => pKeys.includes(k))
        .filter(([, v]) => v !== undefined && v !== null && String(v) !== '')
    );

    const draftIdNum =
      draftId != null && String(draftId).trim() !== '' ? Number(draftId) : null;

    const payload = {
      ...(Number.isFinite(draftIdNum) ? { id: draftIdNum } : {}),
      forkliftId: forkliftData.id || null,
      customerName: vals.customer_name || forkliftData.customerName || '',
      customerPO: vals.customer_po || '',
      address: vals.address || forkliftData.address || '',
      city: vals.city || forkliftData.city || '',
      state: vals.state || forkliftData.state || '',
      unitId: vals.unit_number || forkliftData.unitId || unitId || '',
      make: vals.make || forkliftData.make || '',
      model: vals.model || forkliftData.model || '',
      serialNumber: vals.serial_number || forkliftData.serialNumber || '',
            hourMeter: (() => {
              const hm =
                vals.hour_meter !== undefined &&
                vals.hour_meter !== null &&
                String(vals.hour_meter).trim() !== ''
                  ? vals.hour_meter
                  : forkliftData?.hourMeter ?? 0;

              const n = Number(hm);
              return Number.isFinite(n) ? n : 0;
            })(),


      dateStarted: fmtDate(vals.dateStarted) || fmtDate(dayjs()),
      dateCompleted: fmtDate(vals.dateCompleted) || fmtDate(dayjs()),
      inspectionData,
      technicianComments: vals.technician_comments || '',
      technicianSignature: vals.technician_signature || '',
      description: vals.description || 'Draft maintenance saved via frontend.',
    };

    const isRetired = String(forkliftData?.status || '').toUpperCase() === 'RETIRED';

    if (isRetired) {
      await saveDraftWithStatus('RETIRED', payload);
      return;
    }

    setPendingDraftPayload(payload);
    setDraftStatusOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const vals = await form.validateFields();
      await onFinish(vals);
    } catch (err) {
      const errorFields = err?.errorFields || [];
      if (!errorFields.length) return;

      const first = errorFields[0].name;
      form.scrollToField(first, { behavior: 'smooth', block: 'center' });

      requestAnimationFrame(() => {
        const idSuffix = Array.isArray(first) ? first.join('_') : first;
        const el =
          document.querySelector(`[id$="${idSuffix}"]`) || form.getFieldInstance?.(first);

        if (el && typeof el.focus === 'function') {
          el.focus({ preventScroll: true });
        }
      });
    }
  };

  const radioItem = (name, label, required = true) => (
    <Form.Item
      key={name}
      label={label}
      name={name}
      rules={required ? [{ required: true, message: 'Required' }] : []}
    >
      {PFN}
    </Form.Item>
  );

  const watchedValues = Form.useWatch([], form);
  const allVals = useMemo(() => watchedValues || {}, [watchedValues]);
  const pfnMap = Object.fromEntries(pKeys.map((k) => [k, allVals[k]]));

  useEffect(() => {
    if (!allVals || Object.keys(allVals).length === 0) return;
    if (!dirtyInitRef.current) {
      dirtyInitRef.current = true;
      return;
    }
    setIsDirty(true);
    try {
      localStorage.setItem('dirtyForm', '1');
    } catch {}
  }, [allVals]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const customerName = forkliftData.customerName || resolveActiveCustomerContext(session).customerName;

  const handleResultClose = () => {
    setResultOpen(false);
    if (resultStatus !== 'ERROR') {
      navigate(recordReturnTarget, { replace: true, state: { customerId } });
    }
  };

  const requestLeave = useCallback(
    (action) => {
      if (!isDirty) {
        action();
        return;
      }
      pendingNavRef.current = action;
      setUnsavedOpen(true);
    },
    [isDirty]
  );

  const handleCancel = () => {
    requestLeave(() =>
      navigate(recordReturnTarget, { replace: true, state: { customerId } })
    );
  };

  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      const sider = e.target.closest('.ant-layout-sider');
      if (!sider) return;
      const menuItem = e.target.closest('.ant-menu-item');
      if (!menuItem) return;
      const link = menuItem.querySelector('a[href]');
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();

      const href = link.getAttribute('href');
      pendingNavRef.current = () => navigate(href);
      setUnsavedOpen(true);
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [isDirty, navigate]);

  useEffect(() => {
    if (!isDirty) return;

    const currentUrl = window.location.href;

    const handlePopState = () => {
      window.history.pushState(null, '', currentUrl);
      requestLeave(() => {
        window.removeEventListener('popstate', handlePopState);
        navigate(-2);
      });
    };

    window.history.pushState(null, '', currentUrl);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDirty, navigate, requestLeave]);

  const renderPathChoice = () => (
    <DashboardPageShell
      title={customerName || 'Maintenance Checklist'}
      productDashboardShell="customer"
      headerClassName="dashboard-fleet-header"
      contentClassName="dashboard-fleet-page maintenance-choice-page"
    >
      <DashboardPanel className="maintenance-choice-panel">
        <div className="maintenance-choice-heading">
          <Typography.Title level={3}>
            Submit maintenance record
          </Typography.Title>
          <Typography.Paragraph className="maintenance-choice-helper">
            Choose how to record maintenance for this unit.
          </Typography.Paragraph>
        </div>

        <div
          className="maintenance-choice-options"
          aria-label="Maintenance record submission options"
        >
          <article className="maintenance-choice-card maintenance-choice-card-featured">
            <div className="maintenance-choice-card-copy">
              <span
                className="maintenance-choice-icon maintenance-choice-icon-primary"
                aria-hidden="true"
              >
                <CheckCircleOutlined />
              </span>
              <div>
                <Typography.Text className="maintenance-choice-card-eyebrow">
                  Recommended
                </Typography.Text>
                <Typography.Title level={3}>Use ManageLift form</Typography.Title>
                <Typography.Paragraph>
                  Complete the built-in maintenance checklist and save it directly to this unit’s records.
                </Typography.Paragraph>
              </div>
            </div>
            <DashboardPrimaryActionButton
              size="large"
              onClick={() => setFormPath('native')}
              className="maintenance-choice-button maintenance-choice-primary"
            >
              Start maintenance checklist
              <ArrowRightOutlined />
            </DashboardPrimaryActionButton>
          </article>

          <article className="maintenance-choice-card">
            <div className="maintenance-choice-card-copy">
              <span className="maintenance-choice-icon" aria-hidden="true">
                <UploadOutlined />
              </span>
              <div>
                <Typography.Text className="maintenance-choice-card-eyebrow">
                  External form
                </Typography.Text>
                <Typography.Title level={3}>Use my own form</Typography.Title>
                <Typography.Paragraph>
                  Upload a completed external maintenance form and keep it with this unit’s history.
                </Typography.Paragraph>
              </div>
            </div>
            <Button
              size="large"
              onClick={() => setFormPath('external')}
              className="maintenance-choice-button maintenance-choice-secondary"
            >
              Submit external form
              <ArrowRightOutlined />
            </Button>
          </article>
        </div>

        <div className="maintenance-choice-footer">
          <Typography.Text className="maintenance-choice-footer-text">
            Not ready to submit? Return to records.
          </Typography.Text>
          <Button
            size="large"
            onClick={handleCancel}
            className="maintenance-choice-cancel"
          >
            Cancel
          </Button>
        </div>
      </DashboardPanel>
    </DashboardPageShell>
  );

  const renderExternalForm = () => (
    <>
      <DashboardPageShell
        title={customerName || 'Maintenance Checklist'}
        productDashboardShell="customer"
        headerClassName="dashboard-fleet-header"
        contentClassName="dashboard-fleet-page maintenance-external-page"
        contentInnerClassName="maintenance-external-shell"
      >
        <DashboardPanel className="maintenance-external-panel">
          <div className="maintenance-external-heading">
            <Typography.Title level={3}>Submit external maintenance form</Typography.Title>
            <Typography.Paragraph type="secondary" className="maintenance-external-helper">
              Upload the completed maintenance form, then add optional lift or work photos.
            </Typography.Paragraph>
          </div>
          <Card title="External maintenance details" className="maintenance-external-card">
            <Form form={externalForm} layout="vertical" onFinish={submitExternalForm} initialValues={{ date: dayjs(), status: 'IN_SERVICE' }} className="maintenance-external-form">
              <Row gutter={16}>
                <Col xs={24} md={12}><Form.Item label="Customer" name="customer_name"><Input disabled /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item label="Unit ID" name="unit_number" rules={[{ required: true, message: 'Unit ID required' }]}><Input disabled /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item label="Date" name="date" rules={[{ required: true, message: 'Date required' }]}><DatePicker className="maintenance-external-date-picker" allowClear={false} disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item label="Hour Meter" name="hour_meter" rules={[{ required: true, message: 'Hour meter required' }]}><Input type="number" min={initialHour ?? 0} /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item label="Work Result / Status" name="status" rules={[{ required: true, message: 'Status required' }]}><Select options={[{ value: 'IN_SERVICE', label: 'In service' }, { value: 'DOWN', label: 'Down' }, { value: 'WAITING_ON_PARTS', label: 'Waiting on parts' }]} /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item label="Customer PO" name="customer_po"><Input /></Form.Item></Col>
                <Col xs={24}><Form.Item label="Notes / Summary" name="technician_comments"><Input.TextArea rows={4} /></Form.Item></Col>
              </Row>
              <div className="maintenance-external-signature-row">
                <Form.Item label="Technician Signature" name="technician_signature" rules={[{ required: true, message: 'Signature required' }]} className="maintenance-external-signature-item"><Input /></Form.Item>
              </div>
              <div className="maintenance-external-attachments">
                <RecordAttachments ref={externalAttachmentsRef} recordType="MAINTENANCE" recordId={savedRecordId} readOnly={false} attachmentTypes={['EXTERNAL_SUBMITTED_FORM', 'PHOTO']} variant="uploadSection" />
              </div>
              <div className="maintenance-external-actions"><Button type="primary" htmlType="submit" loading={submitting}>Submit external form</Button><Button onClick={handleCancel} disabled={submitting}>Cancel</Button></div>
            </Form>
          </Card>
        </DashboardPanel>
      </DashboardPageShell>
      <Modal
        open={resultOpen}
        title={resultTitle}
        onOk={handleResultClose}
        onCancel={handleResultClose}
        footer={[
          <Button key="ok" type="primary" onClick={handleResultClose}>
            OK
          </Button>,
        ]}
      >
        <Typography.Paragraph className="maintenance-result-message">{resultMessage}</Typography.Paragraph>
      </Modal>
    </>
  );

  if (!isReadOnly && !recordId && !draftId && !formPath) return renderPathChoice();
  if (!isReadOnly && !recordId && !draftId && formPath === 'external') return renderExternalForm();

  const isExternalFormRecord = isReadOnly && submissionSource === 'EXTERNAL_FORM';
  const formatMetadataDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '');
  const formatMetadataDateTime = (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '');
  const maintenanceDetail = maintenanceRecordDetail || {};
  const externalMaintenanceDate = maintenanceDetail.dateCompleted || maintenanceDetail.dateStarted;
  const externalMaintenanceSubmittedAt = maintenanceDetail.createdAt || maintenanceDetail.submittedAt || '';
  const maintenanceMetadata = [
    { label: 'Customer', value: isExternalFormRecord ? maintenanceDetail.customerName || '' : allVals.customer_name || '' },
    ...(!isExternalFormRecord ? [{ label: 'Customer PO', value: allVals.customer_po || '' }] : []),
    { label: 'Unit ID', value: isExternalFormRecord ? maintenanceDetail.unitId || '' : allVals.unit_number || '' },
    { label: 'Make', value: isExternalFormRecord ? maintenanceDetail.make || '' : allVals.make || '' },
    { label: 'Model', value: isExternalFormRecord ? maintenanceDetail.model || '' : allVals.model || '' },
    { label: 'Serial Number', value: isExternalFormRecord ? maintenanceDetail.serialNumber || '' : allVals.serial_number || '' },
    ...(isExternalFormRecord
      ? [{ label: 'Date', value: formatMetadataDate(externalMaintenanceDate) }]
      : [
          { label: 'Date Started', value: formatMetadataDate(allVals.dateStarted) },
          { label: 'Date Completed', value: formatMetadataDate(allVals.dateCompleted) },
        ]),
    { label: 'Hour Meter', value: isExternalFormRecord ? maintenanceDetail.hourMeter ?? '' : allVals.hour_meter ?? '' },
    { label: 'Notes / Summary', value: isExternalFormRecord ? maintenanceDetail.technicianComments || '' : allVals.technician_comments || '' },
    { label: 'Technician Signature', value: isExternalFormRecord ? maintenanceDetail.technicianSignature || '' : allVals.technician_signature || '' },
    ...(isExternalFormRecord ? [{ label: 'Submitted At', value: formatMetadataDateTime(externalMaintenanceSubmittedAt) }] : []),
  ];

  return (
    <>
      <div className="screen-only">
        <DashboardPageShell
        title={customerName || 'Maintenance Checklist'}
        productDashboardShell="customer"
        headerClassName="dashboard-fleet-header"
        contentClassName="dashboard-fleet-page maintenance-form-page"
        contentInnerClassName="maintenance-form-shell"
      >
        <DashboardPanel className="maintenance-form-panel">
            <div className="maintenance-form-heading">
              <div className="maintenance-title-group">
                <Typography.Title level={2}>{isExternalFormRecord ? 'Submitted Maintenance Checklist' : 'Maintenance Checklist'}</Typography.Title>
                <Typography.Paragraph className="maintenance-form-helper">
                  {isExternalFormRecord
                    ? 'Review the submitted external form, captured record details, and related attachments.'
                    : 'Perform scheduled service, record inspection results, and update status for this unit.'}
                </Typography.Paragraph>
              </div>

              {performedByName && !isExternalFormRecord && (
                <Typography.Text className="maintenance-performed-by">
                  Performed By: {performedByName}
                </Typography.Text>
              )}
            </div>

            {isExternalFormRecord ? (
              <ExternalSubmittedFormRecord
                formName="Maintenance Checklist"
                recordType="MAINTENANCE"
                recordId={recordId || savedRecordId}
                metadata={maintenanceMetadata}
                performedByLabel="Technician"
                performedByName={performedByName}
                notesSignatureTitle="Notes & Technician Signature"
                signatureLabel="Technician Signature"
                onPrint={() => window.print()}
                onCancel={handleCancel}
              />
            ) : (
            <>
            <Form
              className="maintenance-native-form"
              form={form}
              layout="vertical"
              onFinish={isReadOnly ? undefined : onFinish}
              disabled={isReadOnly}
              onFinishFailed={({ errorFields }) => {
                if (!errorFields?.length) return;

                const first = errorFields[0].name;

                form.scrollToField(first, { behavior: 'smooth', block: 'center' });

                requestAnimationFrame(() => {
                  const idSuffix = Array.isArray(first) ? first.join('_') : first;
                  const el =
                    document.querySelector(`[id$="${idSuffix}"]`) ||
                    form.getFieldInstance?.(first);

                  if (el && typeof el.focus === 'function') {
                    el.focus({ preventScroll: true });
                  }
                });
              }}
            >
              <Card className="maintenance-section-card" title="Equipment & Customer Details">
                <Row gutter={16} className="eq-print-grid">
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Customer Name" name="customer_name">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Address" name="address">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="City" name="city">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="State" name="state">
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Customer PO" name="customer_po">
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label="Date Started"
                      name="dateStarted"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <DatePicker
                        className="maintenance-date-picker"
                        allowClear={false}
                        inputReadOnly
                        disabledDate={(d) => !d || !d.isSame(dayjs(), 'day')}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label="Date Completed"
                      name="dateCompleted"
                      dependencies={['dateStarted']}
                      rules={[
                        { required: true, message: 'Required' },
                        {
                          validator: (_, value) => {
                            const start = form.getFieldValue('dateStarted');
                            if (!value || !start) return Promise.resolve();
                            if (value.isBefore(start, 'day')) {
                              return Promise.reject(
                                new Error('Completed must be on or after Date Started')
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <DatePicker
                        className="maintenance-date-picker"
                        allowClear={false}
                        inputReadOnly
                        disabledDate={(d) => !d || !d.isSame(dayjs(), 'day')}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Unit ID#" name="unit_number">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Make" name="make">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Model" name="model">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Form.Item label="Serial Number" name="serial_number">
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12} lg={6}>
                    <Form.Item
                      label={
                        <span>
                          Hour Meter{' '}
                          <Tooltip title={`Current: ${initialHour ?? '—'}`}>
                            <InfoCircleOutlined className="maintenance-info-icon" />
                          </Tooltip>
                        </span>
                      }
                      name="hour_meter"
                      rules={[
                        { required: true, message: 'Hour meter required' },
                        () => ({
                          validator: () => {
                            if (!hourTouched) {
                              return Promise.reject(
                                new Error('Confirm the hour meter (enter the same or a higher value)')
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                        {
                          validator: (_, value) => {
                            const num = Number(value);
                            if (!Number.isFinite(num)) {
                              return Promise.reject(new Error('Enter a number'));
                            }
                            if (initialHour != null && num < initialHour) {
                              return Promise.reject(new Error(`Must be ≥ ${initialHour}`));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        inputMode="numeric"
                        type="number"
                        onChange={() => setHourTouched(true)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Divider orientation="left" className="legend-divider">
                Legend
              </Divider>
              <Typography.Text className="legend-text">
                P = Pass, F = Fail, N = Not Applicable
              </Typography.Text>

              <div className="section-stack">
                <Card className="maintenance-section-card maintenance-inspection-card" title="1. Visual Inspection">
                  <Row gutter={16}>
                    {[
                      ['fluid_leaks', 'Fluid Leaks'],
                      ['carriage_rollers_slide_blocks', 'Carriage Rollers / Slide Blocks'],
                      ['mast_chains_retainers_sheaves_anchors', 'Mast – Chains / Retainers / Sheaves / Anchors'],
                      ['forks_and_fork_lock_pins', 'Forks and Fork Lock Pins'],
                      ['hydraulic_cylinders', 'Hydraulic Cylinders'],
                      ['brake_master_cylinder', 'Brake Master Cylinder'],
                      ['over_mast_hoses_and_cables', 'Over Mast Hoses and Cables'],
                      ['caster_assembly', 'Caster Assembly'],
                      ['drive_unit_radial_rings', 'Drive Unit / Radial Rings'],
                      ['hydraulic_manifold_piping', 'Hydraulic Manifold / Piping'],
                      ['tractor_frame_base_legs_doors_and_covers', 'Tractor Frame / Base Legs / Doors & Covers'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={8} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card maintenance-inspection-card" title="2. Hydraulic System">
                  <Row gutter={16}>
                    {[
                      ['lift_cyl', 'Lift Cyl.'],
                      ['reach_cyl', 'Reach Cyl.'],
                      ['tilt_cyl', 'Tilt Cyl.'],
                      ['sideshift_cyl', 'Sideshift Cyl.'],
                      ['connections', 'Connections'],
                      ['relief_pressures', 'Relief Pressures'],
                      ['lift', 'Lift'],
                      ['aux', 'Aux'],
                      ['steer', 'Steer'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={8} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card maintenance-inspection-card" title="3. Electrical System">
                  <Row gutter={16}>
                    {[
                      ['control_wiring', 'Control Wiring'],
                      ['switches', 'Switches'],
                      ['contactors', 'Contactors'],
                      ['power_cables', 'Power Cables'],
                      ['fuses', 'Fuses'],
                      ['connectors', 'Connectors'],
                      ['system_short_to_frame', 'System Short to Frame'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={8} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card maintenance-inspection-card" title="4. Motors — Drive">
                  <Row gutter={16}>
                    {[
                      ['drive_brushes', 'Brushes'],
                      ['drive_comm', 'Comm'],
                      ['drive_amps', 'Amps'],
                      ['drive_ohms', 'Ohms'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={6} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card maintenance-inspection-card" title="4. Motors — Lift">
                  <Row gutter={16}>
                    {[
                      ['lift_brushes', 'Brushes'],
                      ['lift_comm', 'Comm'],
                      ['lift_amps', 'Amps'],
                      ['lift_ohms', 'Ohms'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={6} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card maintenance-inspection-card" title="4. Motors — Aux">
                  <Row gutter={16}>
                    {[
                      ['aux_brushes', 'Brushes'],
                      ['aux_comm', 'Comm'],
                      ['aux_amps', 'Amps'],
                      ['aux_ohms', 'Ohms'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={6} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card maintenance-inspection-card" title="4. Motors — Steer">
                  <Row gutter={16}>
                    {[
                      ['steer_brushes', 'Brushes'],
                      ['steer_comm', 'Comm'],
                      ['steer_amps', 'Amps'],
                      ['steer_ohms', 'Ohms'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={6} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card maintenance-inspection-card" title="5. Battery">
                  <Row gutter={16}>
                    {[
                      ['volts_loaded', 'Volts Loaded'],
                      ['volts_no_load', 'Volts No Load'],
                      ['pos_leakage', 'Pos. Leakage'],
                      ['neg_leakage', 'Neg. Leakage'],
                      ['sg', 'S.G.'],
                      ['electrolyte_level', 'Electrolyte level'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={8} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card maintenance-inspection-card" title="6. Operational Inspection">
                  <Row gutter={16}>
                    {[
                      ['braking_distance', 'Braking Distance'],
                      ['plugging_distance', 'Plugging Distance'],
                      ['travel_system', 'Travel System'],
                      ['lift_lower_system', 'Lift / Lower System'],
                      ['auxiliary_system', 'Auxiliary System'],
                      ['steering_system', 'Steering System'],
                      ['mini_mast_functions', 'Mini Mast Functions'],
                      ['wire_guidance', 'Wire Guidance'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={8} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card maintenance-inspection-card" title="7. Safety">
                  <Row gutter={16}>
                    {[
                      ['horn', 'Horn'],
                      ['travel_alarm', 'Travel Alarm'],
                      ['lights_warning_brake', 'Lights – Warning / Brake'],
                      ['flood_lights', 'Flood Lights'],
                      ['emergency_disconnect', 'Emergency Disconnect'],
                      ['static_straps', 'Static Straps'],
                      ['mast_stops_attach_bolts', 'Mast – Stops / Attach Bolts'],
                      ['load_backrest', 'Load Backrest'],
                      ['fork_condition', 'Fork Condition'],
                      ['lift_chains', 'Lift Chains'],
                      ['brake_pads_shoes', 'Brake Pads / Shoes'],
                      ['brake_disk_drums', 'Brake Disk / Drums'],
                      ['parking_brake', 'Parking Brake'],
                      ['steering_system_safety', 'Steering System'],
                      ['warning_decals', 'Warning Decals'],
                      ['data_plate', 'Data Plate'],
                      ['tire_condition', 'Tire Condition'],
                      ['overhead_guard', 'Overhead Guard'],
                      ['safety_belt_tether', 'Safety Belt / Tether'],
                      ['safety_glass', 'Safety Glass'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={8} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card maintenance-inspection-card" title="8. Lubrication">
                  <Row gutter={16}>
                    {[
                      ['hydraulic_fluid', 'Hydraulic Fluid'],
                      ['oil_filter', 'Oil Filter'],
                      ['drive_unit_gear_oil', 'Drive Unit / Gear Oil'],
                      ['brake_fluid_reservoir', 'Brake Fluid Reservoir'],
                      ['steer_linkage', 'Steer Linkage'],
                      ['mast_chains_bearings', 'Mast / Chains / Bearings'],
                      ['scissor_assembly', 'Scissor Assembly'],
                      ['carriage_attachments', 'Carriage / Attachments'],
                      ['load_wheels', 'Load Wheels'],
                      ['caster_unit', 'Caster Unit'],
                      ['battery_rollers', 'Battery Rollers'],
                      ['pump_couplings', 'Pump Couplings'],
                    ].map(([n, l]) => (
                      <Col xs={24} md={12} lg={8} key={n}>
                        {radioItem(n, l)}
                      </Col>
                    ))}
                  </Row>
                </Card>

                <Card className="maintenance-section-card" title="Technician Comments & Signature">
                  <Form.Item label="Technician Comments:" name="technician_comments">
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <div className="maintenance-signature-row">
                    <Form.Item
                      label="Technician Signature"
                      name="technician_signature"
                      rules={[{ required: true, message: 'Signature required' }]}
                      className="maintenance-signature-item"
                    >
                      <Input className="sig-input" />
                    </Form.Item>
                  </div>

                  {!isReadOnly && (
                    <div className="maintenance-actions">
                      <DashboardPrimaryActionButton
                        onClick={handleSubmit}
                        loading={submitting}
                        disabled={submitting}
                      >
                        Submit
                      </DashboardPrimaryActionButton>
                      <Button
                        type="primary"
                        onClick={handleFinishLater}
                        loading={submitting}
                        disabled={submitting}
                      >
                        Finish later
                      </Button>
                      <Button onClick={handleCancel} disabled={submitting}>
                        Cancel
                      </Button>
                    </div>
                  )}
                </Card>
              </div>
            </Form>
            {isReadOnly ? (
              <>
                <Card className="maintenance-section-card maintenance-attachments" title="Attachments">
                  <div className="maintenance-readonly-attachments">
                    <section className="maintenance-readonly-attachment-single" aria-labelledby="maintenance-photo-attachments-heading">
                      <h3 id="maintenance-photo-attachments-heading">Photos / Attachments</h3>
                      <RecordAttachments
                        ref={attachmentsRef}
                        recordType="MAINTENANCE"
                        recordId={recordId || savedRecordId}
                        readOnly
                        attachmentTypes={['PHOTO']}
                        variant="compactReadOnlySection"
                        onUploadFailed={() => Modal.warning({ title: 'Record saved', content: 'Record was saved, but one or more uploads failed during submission. Uploads can only be added during the active submission flow.' })}
                      />
                    </section>
                  </div>
                </Card>
                <div className="maintenance-actions maintenance-readonly-actions">
                  <DashboardPrimaryActionButton
                    icon={<PrinterOutlined />}
                    onClick={() => window.print()}
                    className="maintenance-readonly-action maintenance-readonly-print-action"
                  >
                    Print
                  </DashboardPrimaryActionButton>
                  <Button
                    onClick={handleCancel}
                    disabled={submitting}
                    className="dashboard-secondary-action-button maintenance-readonly-action"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <div className="maintenance-attachments">
                <RecordAttachments ref={attachmentsRef} recordType="MAINTENANCE" recordId={recordId || savedRecordId} readOnly={isReadOnly} attachmentTypes={['PHOTO']} onUploadFailed={() => Modal.warning({ title: 'Record saved', content: 'Record was saved, but one or more uploads failed during submission. Uploads can only be added during the active submission flow.' })} />
              </div>
            )}
            </>
            )}
        </DashboardPanel>

        <Modal
          open={resultOpen}
          title={resultTitle}
          onOk={handleResultClose}
          onCancel={handleResultClose}
          footer={[
            <Button key="ok" type="primary" onClick={handleResultClose}>
              OK
            </Button>,
          ]}
        >
          <Typography.Paragraph className="maintenance-result-message">{resultMessage}</Typography.Paragraph>
        </Modal>

        <Modal
          open={choiceOpen}
          title="Failed checks detected"
          onCancel={() => setChoiceOpen(false)}
          footer={[
            <Button
              key="down"
              type="primary"
              onClick={() => {
                setChoiceOpen(false);
                submitWith('DOWN', pendingPayload, pendingInspection);
              }}

            >
              Down
            </Button>,
            <Button
              key="waiting"
              onClick={() => {
                setChoiceOpen(false);
                submitWith('WAITING_ON_PARTS', pendingPayload, pendingInspection);
              }}

            >
              Down – Waiting on Parts
            </Button>,
            <Button key="cancel" onClick={() => setChoiceOpen(false)} >
              Cancel
            </Button>,
          ]}
          centered
          maskClosable={false}
        >
          Select the status for this lift.
        </Modal>

        <Modal
          open={draftStatusOpen}
          title="Update Forklift Status"
          onCancel={() => {
            setDraftStatusOpen(false);
            setPendingDraftPayload(null);
          }}
          footer={[
            <Button
              key="inservice"
              type="primary"
              onClick={() => {
                setDraftStatusOpen(false);
                saveDraftWithStatus('IN_SERVICE');
              }}

            >
              In Service
            </Button>,
            <Button
              key="down"
              onClick={() => {
                setDraftStatusOpen(false);
                saveDraftWithStatus('DOWN');
              }}

            >
              Down
            </Button>,
            <Button
              key="waiting"
              onClick={() => {
                setDraftStatusOpen(false);
                saveDraftWithStatus('WAITING_ON_PARTS');
              }}

            >
              Down — Waiting on Parts
            </Button>,
            <Button
              key="cancel"
              onClick={() => {
                setDraftStatusOpen(false);
                setPendingDraftPayload(null);
              }}

            >
              Cancel
            </Button>,
          ]}
          centered
          maskClosable={false}
        >
          Select the status for this lift.
        </Modal>

        <Modal
          open={unsavedOpen}
          title="Unsaved Changes"
          onCancel={() => {
            setUnsavedOpen(false);
            pendingNavRef.current = null;
          }}
          footer={[
            <Button
              key="stay"
              type="primary"

              onClick={() => {
                setUnsavedOpen(false);
                pendingNavRef.current = null;
              }}
            >
              Stay on Page
            </Button>,
            <Button
              key="leave"
              type="primary"
              danger

              onClick={() => {
                const action = pendingNavRef.current;
                setUnsavedOpen(false);
                pendingNavRef.current = null;
                try {
                  localStorage.removeItem('dirtyForm');
                } catch {}
                setIsDirty(false);
                if (action) action();
              }}
            >
              Leave Page
            </Button>,
          ]}
          centered
          maskClosable={false}
        >
          You have unsaved changes on this form. If you leave this page, your entries will not be
          saved.
        </Modal>
        </DashboardPageShell>
      </div>

      <div className="print-only" aria-hidden="true">
        {isExternalFormRecord ? (
          <PrintExternalSubmittedFormSummary
            formName="Maintenance Checklist"
            metadata={maintenanceMetadata}
            performedByLabel="Performed by"
            performedByName={performedByName}
          />
        ) : (
        <PrintMaintenanceForm
          header={{
            customerName: allVals.customer_name || '',
            address: allVals.address || '',
            city: allVals.city || '',
            state: allVals.state || '',
            customerPO: allVals.customer_po || '',
            dateStarted: allVals.dateStarted || '',
            dateCompleted: allVals.dateCompleted || '',
            unitId: allVals.unit_number || '',
            make: allVals.make || '',
            model: allVals.model || '',
            serialNumber: allVals.serial_number || '',
            hourMeter: allVals.hour_meter || '',
            performedByName: performedByName,
            recordId: recordId || savedRecordId || '',
            submittedAt: maintenanceRecordDetail?.createdAt || maintenanceRecordDetail?.submittedAt || '',
          }}
          sections={PRINT_SECTIONS}
          pfn={pfnMap}
          footer={{
            comments: allVals.technician_comments || '',
            signature: allVals.technician_signature || '',
          }}
        />
        )}
      </div>
    </>
  );
}
